import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { shareResultsViaEmail } from '../services/api';

const ShareResultsModal = ({ isOpen, onClose, results }) => {
  const [emails, setEmails] = useState('');
  const [emailList, setEmailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showFileUpload, setShowFileUpload] = useState(false);
  const resultsContainerRef = useRef(null);

  const handleAddEmail = () => {
    const newEmails = emails
      .split(/[,;\n]/)
      .map(e => e.trim())
      .filter(e => e && validateEmail(e));

    if (newEmails.length > 0) {
      setEmailList([...emailList, ...newEmails]);
      setEmails('');
      setError('');
    } else {
      setError('Please enter valid email addresses');
    }
  };

  const handleRemoveEmail = (index) => {
    setEmailList(emailList.filter((_, i) => i !== index));
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const parsedEmails = text
          .split(/[\n,]/)
          .map(e => e.trim())
          .filter(e => e && validateEmail(e));

        if (parsedEmails.length > 0) {
          setEmailList([...emailList, ...parsedEmails]);
          setSuccess(`Added ${parsedEmails.length} email(s) from file`);
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('No valid emails found in file');
        }
      } catch (err) {
        setError('Failed to parse file');
      }
    };
    reader.readAsText(file);
  };

  const generatePDF = async () => {
    try {
      // Try multiple selectors to find the results container
      let element = document.querySelector('.results-container');
      if (!element) {
        element = document.querySelector('[class*="results"]');
      }
      if (!element) {
        element = document.querySelector('main');
      }
      
      if (!element) {
        throw new Error('Could not find results container. Please try again.');
      }

      console.log('📄 Generating PDF from element:', element.className);
      
      // Hide elements that should not be in the PDF
      const actionsDiv = document.querySelector('.results-actions');
      const modalBackdrop = document.querySelector('.modal-backdrop');
      
      const originalActionStyles = {};
      const originalModalStyles = {};
      
      if (actionsDiv) {
        originalActionStyles.display = actionsDiv.style.display;
        actionsDiv.style.display = 'none';
        console.log('   ✓ Hidden results-actions buttons');
      }
      
      if (modalBackdrop) {
        originalModalStyles.display = modalBackdrop.style.display;
        modalBackdrop.style.display = 'none';
        console.log('   ✓ Hidden modal');
      }
      
      // Use lower scale and settings to minimize file size
      const canvas = await html2canvas(element, {
        scale: 1.5, // Reduced from 2 for smaller file size
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: true,
        quality: 0.85 // Add quality setting
      });

      // Restore hidden elements
      if (actionsDiv) {
        actionsDiv.style.display = originalActionStyles.display;
      }
      if (modalBackdrop) {
        modalBackdrop.style.display = originalModalStyles.display;
      }

      // Convert to JPEG for better compression instead of PNG
      const imgData = canvas.toDataURL('image/jpeg', 0.85);
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      let heightLeft = imgHeight;
      let position = 0;

      // Use JPEG for the first image with compression
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdf.internal.pageSize.getHeight();
      }

      // Compress the PDF
      const pdfBase64 = pdf.output('dataurlstring').split(',')[1];
      console.log('✅ PDF generated successfully, size:', Math.round(pdfBase64.length / 1024 / 1024 * 100) / 100, 'MB');
      return pdfBase64;
    } catch (error) {
      console.error('❌ PDF generation error:', error.message);
      throw new Error('Failed to generate PDF: ' + error.message);
    }
  };

  const handleSendShare = async () => {
    if (emailList.length === 0) {
      setError('Please add at least one email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log('� Starting share results send...');
      console.log('   Recipients:', emailList.length);
      
      // Step 1: Generate PDF with timeout
      console.log('   Step 1: Generating PDF...');
      let pdfData;
      try {
        const pdfPromise = generatePDF();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('PDF generation timeout - taking too long')), 30000)
        );
        pdfData = await Promise.race([pdfPromise, timeoutPromise]);
      } catch (pdfErr) {
        throw new Error('PDF Generation Failed: ' + pdfErr.message);
      }
      
      if (!pdfData || pdfData.length === 0) {
        throw new Error('PDF was generated but appears to be empty');
      }

      // Step 2: Prepare data
      console.log('   Step 2: Preparing email payload...');
      const payload = {
        recipients: emailList,
        userProfile: results.nickname || 'Creative Professional',
        userScore: results.creativeWorkScore || 50,
        pdfAttachment: pdfData,
        pdfFilename: `Creative-Footprint-${(results.nickname || 'Report').replace(/\s+/g, '-')}.pdf`
      };
      
      console.log('   Payload prepared. PDF size:', pdfData.length, 'bytes');

      // Step 3: Send API request
      console.log('   Step 3: Sending to server...');
      const response = await shareResultsViaEmail(payload);
      
      console.log('   API Response:', response);

      if (response && response.sent > 0) {
        const sentCount = response.sent;
        const totalCount = response.total;
        setSuccess(`✅ Sent to ${sentCount} of ${totalCount} recipient(s)`);
        setEmailList([]);
        
        setTimeout(() => {
          setSuccess('');
          onClose();
        }, 3000);
      } else if (response && response.results) {
        const failedResults = response.results.filter(r => !r.sent);
        const errorMsg = failedResults[0]?.error || 'Unknown error sending emails';
        throw new Error(errorMsg);
      } else {
        throw new Error('No response from server');
      }
    } catch (err) {
      console.error('❌ Share results error:', err);
      const errorMsg = err.message || 'Failed to send results';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Share Your Results</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {error && <div style={{ background: '#fee2e2', color: '#c53030', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</div>}
          {success && <div style={{ background: '#d1fae5', color: '#065f46', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.9rem' }}>{success}</div>}

          <div className="form-group">
            <label htmlFor="emails">Email Addresses</label>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <textarea
                id="emails"
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                placeholder="Enter email addresses (comma, semicolon, or line separated)"
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  fontSize: '0.9rem',
                  minHeight: '80px',
                  fontFamily: 'inherit'
                }}
              />
              <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                <button 
                  className="btn btn-secondary" 
                  onClick={handleAddEmail}
                  disabled={!emails.trim()}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Add
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowFileUpload(!showFileUpload)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {showFileUpload ? 'Cancel' : 'Upload'}
                </button>
              </div>
            </div>

            {showFileUpload && (
              <div style={{ marginBottom: '1rem' }}>
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileUpload}
                  style={{ padding: '0.5rem', width: '100%' }}
                />
                <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>
                  Upload CSV or text file with emails (one per line or comma-separated)
                </p>
              </div>
            )}
          </div>

          {emailList.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Recipients ({emailList.length})
              </label>
              <div style={{
                background: '#f5f5f5',
                padding: '1rem',
                borderRadius: '4px',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {emailList.map((email, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.75rem',
                      background: 'white',
                      marginBottom: '0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.9rem'
                    }}
                  >
                    <span>{email}</span>
                    <button
                      onClick={() => handleRemoveEmail(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        padding: '0 0.5rem'
                      }}
                      title="Remove email"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0' }}>
            Your Creative Footprint profile and PDF report will be sent to each recipient with a personalized message.
          </p>
        </div>

        <div className="modal-footer">
          <button 
            className="btn btn-secondary" 
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleSendShare}
            disabled={loading || emailList.length === 0}
          >
            {loading ? 'Sending...' : `Send to ${emailList.length} Recipient${emailList.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareResultsModal;
